import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import Links from '../builder/Links';
import Starters from '../builder/Starters';
import EscalationButtons from '../builder/EscalationButtons';
import { CustomizationTabProps } from './types';
import {
  Settings,
  MapPin,
  ScanSearch,
  Navigation,
  Timer,
  Cookie,
  BellRing,
  Sparkles,
  Clock,
  Trash2,
  Plus,
  X,
} from 'lucide-react';
import { COUNTRY_OPTIONS, countryName } from '../../../utils/countries';
import AgentTabHeader from './AgentTabHeader';

const isValidUrl = (url: string): boolean => {
  if (!url.trim()) return true;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

interface VisibilityRule {
  id: string;
  action: 'show' | 'hide';
  // Rules without a type predate geofencing and are URL rules.
  type?: 'url' | 'country';
  condition?: 'equals' | 'contains';
  url?: string;
  countries?: string[];
}

interface VisibilityRulesProps {
  visibilityRules: VisibilityRule[];
  setVisibilityRules: (rules: VisibilityRule[]) => void;
  showGloballyAfterEngagement: boolean;
  setShowGloballyAfterEngagement: (enabled: boolean) => void;
}

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children?: React.ReactNode;
  badge?: string;
}

const ToggleRow = ({ icon, label, description, checked, onCheckedChange, children, badge }: ToggleRowProps) => (
  <div className={cn(
    'rounded-lg border bg-card p-4 transition-colors',
    checked && 'border-foreground/10'
  )}>
    <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5">{icon}</span>
          <h4 className="text-sm font-medium">{label}</h4>
          {badge && <Badge variant="secondary" className="text-[10px]">{badge}</Badge>}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        {children && <div className="mt-3">{children}</div>}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="shrink-0"
      />
    </div>
  </div>
);

const VisibilityRules = ({
  visibilityRules,
  setVisibilityRules,
  showGloballyAfterEngagement,
  setShowGloballyAfterEngagement,
}: VisibilityRulesProps) => {
  const addRule = () => {
    if (visibilityRules.length >= 10) return;
    const newRule: VisibilityRule = {
      id: Math.random().toString(36).substr(2, 9),
      action: 'show',
      type: 'url',
      condition: 'equals',
      url: ''
    };
    setVisibilityRules([...visibilityRules, newRule]);
  };

  const removeRule = (ruleId: string) => {
    setVisibilityRules(visibilityRules.filter(rule => rule.id !== ruleId));
  };

  const updateRule = (ruleId: string, updates: Partial<VisibilityRule>) => {
    setVisibilityRules(visibilityRules.map(rule =>
      rule.id === ruleId ? { ...rule, ...updates } : rule
    ));
  };

  const setRuleType = (rule: VisibilityRule, type: 'url' | 'country') => {
    if (type === 'country') {
      updateRule(rule.id, { type, condition: undefined, url: undefined, countries: rule.countries ?? [] });
    } else {
      updateRule(rule.id, { type, condition: rule.condition ?? 'equals', url: rule.url ?? '', countries: undefined });
    }
  };

  const addCountry = (rule: VisibilityRule, code: string) => {
    const countries = Array.isArray(rule.countries) ? rule.countries : [];
    if (countries.includes(code)) return;
    updateRule(rule.id, { countries: [...countries, code] });
  };

  const removeCountry = (rule: VisibilityRule, code: string) => {
    const countries = Array.isArray(rule.countries) ? rule.countries : [];
    updateRule(rule.id, { countries: countries.filter((c) => c !== code) });
  };

  return (
    <div className="overflow-hidden rounded-lg border bg-card divide-y">
      {visibilityRules.map((rule, index) => {
        const isCountryRule = rule.type === 'country';
        const ruleUrl = typeof rule.url === 'string' ? rule.url : '';
        const ruleUrlEmpty = !isCountryRule && !ruleUrl.trim();
        const ruleCountries = Array.isArray(rule.countries) ? rule.countries : [];
        const ruleCountriesEmpty = isCountryRule && ruleCountries.length === 0;

        return (
          <div key={rule.id} className="space-y-3 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Rule {index + 1}</span>
              <Button
                onClick={() => removeRule(rule.id)}
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={rule.action}
                onValueChange={(value) => updateRule(rule.id, { action: value as 'show' | 'hide' })}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="show">Show</SelectItem>
                  <SelectItem value="hide">Hide</SelectItem>
                </SelectContent>
              </Select>

              <span className="text-xs text-muted-foreground">if</span>

              <Select
                value={isCountryRule ? 'country' : 'url'}
                onValueChange={(value) => setRuleType(rule, value as 'url' | 'country')}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="country">Country</SelectItem>
                </SelectContent>
              </Select>

              {isCountryRule ? (
                <>
                  <span className="text-xs text-muted-foreground">is one of</span>
                  <Select value="" onValueChange={(code) => addCountry(rule, code)}>
                    <SelectTrigger
                      aria-invalid={ruleCountriesEmpty}
                      className={cn(
                        'flex-1 min-w-[200px]',
                        ruleCountriesEmpty && 'border-destructive focus-visible:ring-destructive/30'
                      )}
                    >
                      <SelectValue placeholder="Add a country..." />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.filter(({ code }) => !ruleCountries.includes(code)).map(({ code, name }) => (
                        <SelectItem key={code} value={code}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ruleCountries.length > 0 && (
                    <div className="basis-full flex items-center gap-1.5 flex-wrap pt-1">
                      {ruleCountries.map((code) => (
                        <Badge key={code} variant="secondary" className="gap-1 pr-1">
                          {countryName(code)}
                          <button
                            type="button"
                            onClick={() => removeCountry(rule, code)}
                            aria-label={`Remove ${countryName(code)}`}
                            className="rounded-sm hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  {ruleCountriesEmpty && (
                    <p className="basis-full text-xs text-destructive">
                      At least one country is required to save this rule.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <Select
                    value={rule.condition ?? 'equals'}
                    onValueChange={(value) => updateRule(rule.id, { condition: value as 'equals' | 'contains' })}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">equals</SelectItem>
                      <SelectItem value="contains">contains</SelectItem>
                    </SelectContent>
                  </Select>

                  <Input
                    value={ruleUrl}
                    onChange={(e) => updateRule(rule.id, { url: e.target.value })}
                    placeholder="Enter URL or URL fragment..."
                    aria-invalid={ruleUrlEmpty}
                    required
                    className={cn(
                      'flex-1 min-w-[200px]',
                      ruleUrlEmpty && 'border-destructive focus-visible:ring-destructive/30'
                    )}
                  />
                  {ruleUrlEmpty && (
                    <p className="basis-full text-xs text-destructive">
                      URL or URL fragment is required to save this rule.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      {visibilityRules.length === 0 && (
        <div className="p-6 text-center">
          <p className="text-sm font-medium">No visibility rules</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your agent appears on every page until a rule is added.
          </p>
        </div>
      )}

      {visibilityRules.length > 0 && visibilityRules.length < 10 && (
        <div className="flex items-center justify-between gap-3 p-3">
          <span className="text-xs text-muted-foreground">
            {visibilityRules.length} of 10 rules
          </span>
          <Button onClick={addRule} variant="outline" size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add rule
          </Button>
        </div>
      )}

      {visibilityRules.length === 0 && (
        <div className="flex justify-end p-3">
          <Button onClick={addRule} variant="outline" size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add rule
          </Button>
        </div>
      )}

      {visibilityRules.length >= 10 && (
        <p className="p-3 text-right text-xs text-muted-foreground">
          Maximum of 10 visibility rules reached.
        </p>
      )}

      {visibilityRules.length > 0 && (
        <div className="flex items-center justify-between gap-6 p-4">
          <div>
            <Label htmlFor="global-after-engagement" className="text-sm font-medium">
              Show globally after engagement
            </Label>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              After a visitor engages, keep the agent available across the site while continuing to enforce country rules.
            </p>
          </div>
          <Switch
            id="global-after-engagement"
            checked={showGloballyAfterEngagement}
            onCheckedChange={setShowGloballyAfterEngagement}
            className="shrink-0"
          />
        </div>
      )}
    </div>
  );
};

interface ContextualExclusionRule {
  id: string;
  condition: 'equals' | 'contains';
  url: string;
}

interface ContextualExclusionRulesProps {
  rules: ContextualExclusionRule[];
  setRules: (rules: ContextualExclusionRule[]) => void;
  contextualActive: boolean;
}

const MAX_CONTEXTUAL_EXCLUSION_RULES = 25;

// URL carve-outs for contextual starters / pop-ups (AIM-501): on a matching
// page the widget shows the agent's default starters and pop-up instead of the
// page-generated ones. Same equals/contains shape as visibility rules.
const ContextualExclusionRules = ({ rules, setRules, contextualActive }: ContextualExclusionRulesProps) => {
  const addRule = () => {
    if (rules.length >= MAX_CONTEXTUAL_EXCLUSION_RULES) return;
    setRules([
      ...rules,
      { id: Math.random().toString(36).substr(2, 9), condition: 'contains', url: '' },
    ]);
  };

  const removeRule = (ruleId: string) => {
    setRules(rules.filter((rule) => rule.id !== ruleId));
  };

  const updateRule = (ruleId: string, updates: Partial<ContextualExclusionRule>) => {
    setRules(rules.map((rule) => (rule.id === ruleId ? { ...rule, ...updates } : rule)));
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5"><Sparkles className="h-4 w-4" /></span>
            <h4 className="text-sm font-medium">Exclude pages from contextual content</h4>
            {rules.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">{rules.length}</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            On matching pages the agent shows its default starters and pop-up instead of ones generated from the page. Use &quot;contains&quot; with a path like <code className="rounded bg-muted px-1">/team/</code> to cover a whole section.
          </p>
          {!contextualActive && (
            <p className="text-xs text-muted-foreground mt-1">
              Contextual starters and contextual pop-ups are both off, so these rules have no effect right now.
            </p>
          )}
        </div>
      </div>

      {rules.length > 0 && (
        <div className="mt-3 space-y-2">
          {rules.map((rule) => {
            const ruleUrl = typeof rule.url === 'string' ? rule.url : '';
            const ruleUrlEmpty = !ruleUrl.trim();
            return (
              <div key={rule.id} className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">URL</span>
                <Select
                  value={rule.condition ?? 'contains'}
                  onValueChange={(value) => updateRule(rule.id, { condition: value as 'equals' | 'contains' })}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contains">contains</SelectItem>
                    <SelectItem value="equals">equals</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={ruleUrl}
                  onChange={(e) => updateRule(rule.id, { url: e.target.value })}
                  placeholder="/team/"
                  aria-invalid={ruleUrlEmpty}
                  className={cn(
                    'flex-1 min-w-[200px]',
                    ruleUrlEmpty && 'border-destructive focus-visible:ring-destructive/30'
                  )}
                />
                <Button
                  onClick={() => removeRule(rule.id)}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  aria-label="Remove rule"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                {ruleUrlEmpty && (
                  <p className="basis-full text-xs text-muted-foreground">
                    Empty rules are dropped when you save.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {rules.length >= MAX_CONTEXTUAL_EXCLUSION_RULES
            ? `Maximum of ${MAX_CONTEXTUAL_EXCLUSION_RULES} rules reached.`
            : rules.length > 0
              ? `${rules.length} of ${MAX_CONTEXTUAL_EXCLUSION_RULES} rules`
              : 'Contextual content shows on every page until a rule is added.'}
        </span>
        {rules.length < MAX_CONTEXTUAL_EXCLUSION_RULES && (
          <Button onClick={addRule} variant="outline" size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add rule
          </Button>
        )}
      </div>
    </div>
  );
};

export default function CustomizationTab({
  agId,
  agentName,
  welcomeMessage,
  setWelcomeMessage,
  links,
  setLinks,
  starters,
  setStarters,
  autoOpenEnabled,
  setAutoOpenEnabled,
  autoOpenSeconds,
  setAutoOpenSeconds,
  popupMessageEnabled,
  setPopupMessageEnabled,
  popupMessage,
  setPopupMessage,
  contextualPopupEnabled,
  setContextualPopupEnabled,
  contextualStartersEnabled,
  setContextualStartersEnabled,
  enableFollowUps,
  setEnableFollowUps,
  followUpInstructions,
  setFollowUpInstructions,
  setNeedsUpdate,
  escalationEnabled,
  setEscalationEnabled,
  escalationButtons,
  setEscalationButtons,
  gdprEnabled,
  setGdprEnabled,
  openOncePerSession,
  setOpenOncePerSession,
  searchBarEnabled,
  setSearchBarEnabled,
  searchBarPlaceholder,
  setSearchBarPlaceholder,
  hideAimdoc,
  setHideAimdoc,
  enableVisitorLocation,
  setEnableVisitorLocation,
  enablePageAwareness,
  setEnablePageAwareness,
  smartNavigationEnabled,
  setSmartNavigationEnabled,
  rateLimitMessages,
  setRateLimitMessages,
  rateLimitSeconds,
  setRateLimitSeconds,
  rateLimitMessage,
  setRateLimitMessage,
  visibilityRules,
  setVisibilityRules,
  contextualExclusionRules,
  setContextualExclusionRules,
  showGloballyAfterEngagement,
  setShowGloballyAfterEngagement,
  disclaimerMessage,
  setDisclaimerMessage,
  privacyPolicyLink,
  setPrivacyPolicyLink
}: CustomizationTabProps) {

  return (
    <div className="w-full">
      <AgentTabHeader
        icon={Settings}
        title="Configuration"
        description="Customize chat behavior, navigation, starters, security, and more."
      />

      <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
      {/* Chat Settings Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Chat settings</h3>
        </div>

        {/* Welcome Message */}
        <div className="rounded-lg border bg-card p-4">
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Welcome message</Label>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                The first message your agent sends to visitors.
              </p>
            </div>
            <Input
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              placeholder="Enter welcome message..."
            />
          </div>
        </div>

        {/* Pop-up Message */}
        <ToggleRow
          icon={<BellRing className="h-4 w-4 text-muted-foreground" />}
          label="Pop-up Message"
          description="Default pop-up message shown next to the collapsed widget."
          checked={popupMessageEnabled}
          onCheckedChange={setPopupMessageEnabled}
        >
          {popupMessageEnabled && (
            <div className="relative">
              <Input
                value={popupMessage}
                onChange={(e) => setPopupMessage(e.target.value)}
                placeholder="Enter pop-up message..."
                maxLength={80}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                {popupMessage.length}/80
              </span>
            </div>
          )}
        </ToggleRow>

        {/* Contextual Pop-ups */}
        {popupMessageEnabled && (
          <ToggleRow
            icon={<Sparkles className="h-4 w-4 text-muted-foreground" />}
            label="Contextual Pop-ups"
            description="AI-generated pop-ups based on the page the visitor is currently on."
            checked={contextualPopupEnabled}
            onCheckedChange={setContextualPopupEnabled}
          />
        )}

        {/* Auto-open */}
        <ToggleRow
          icon={<Timer className="h-4 w-4 text-muted-foreground" />}
          label="Auto-open"
          description="Automatically open the agent after a specified duration."
          checked={autoOpenEnabled}
          onCheckedChange={setAutoOpenEnabled}
        >
          {autoOpenEnabled && (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={autoOpenSeconds}
                onChange={(e) => setAutoOpenSeconds(Number(e.target.value))}
                className="w-20 text-center"
              />
              <span className="text-xs text-muted-foreground">seconds</span>
            </div>
          )}
        </ToggleRow>

        {/* Open Once Per Session */}
        {autoOpenEnabled && (
          <ToggleRow
            icon={<Clock className="h-4 w-4 text-muted-foreground" />}
            label="Open Once Per Session"
            description="The agent will only auto-open once per visitor session."
            checked={openOncePerSession}
            onCheckedChange={setOpenOncePerSession}
          />
        )}

        {/* GDPR Opt-In */}
        <ToggleRow
          icon={<Cookie className="h-4 w-4 text-muted-foreground" />}
          label="GDPR Opt-In"
          description="Require visitor consent before starting the conversation."
          checked={gdprEnabled}
          onCheckedChange={setGdprEnabled}
        />

        {/* Input Bar Placeholder */}
        <div className="rounded-lg border bg-card p-4">
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Input bar placeholder</Label>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                Placeholder text shown in the collapsed widget input bar.
              </p>
            </div>
            <Input
              value={searchBarPlaceholder}
              onChange={(e) => setSearchBarPlaceholder(e.target.value)}
              placeholder="Enter input bar placeholder..."
              maxLength={40}
            />
          </div>
        </div>
      </div>

      {/* Context & Navigation Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Context &amp; navigation</h3>
        </div>

        <ToggleRow
          icon={<MapPin className="h-4 w-4 text-muted-foreground" />}
          label="Visitor Location"
          description="Enable location-based context for more personalized responses."
          checked={enableVisitorLocation}
          onCheckedChange={setEnableVisitorLocation}
        />

        <ToggleRow
          icon={<ScanSearch className="h-4 w-4 text-muted-foreground" />}
          label="Page Awareness"
          description="Enable the agent to understand and reference the current page content."
          checked={enablePageAwareness}
          onCheckedChange={setEnablePageAwareness}
        />

        <ToggleRow
          icon={<Navigation className="h-4 w-4 text-muted-foreground" />}
          label="Smart Navigation"
          description="Allow the agent to navigate visitors to relevant pages on your website."
          checked={smartNavigationEnabled}
          onCheckedChange={setSmartNavigationEnabled}
        />

        <ContextualExclusionRules
          rules={contextualExclusionRules}
          setRules={setContextualExclusionRules}
          contextualActive={contextualStartersEnabled || (popupMessageEnabled && contextualPopupEnabled)}
        />
      </div>

      {/* Links Section */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Links</h3>
            {links.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">{links.length}</Badge>
            )}
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Links displayed at the top of the chat that visitors can open.
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <Links links={links} setLinks={setLinks} setNeedsUpdate={setNeedsUpdate} agId={agId} agentName={agentName} />
        </div>
      </div>

      {/* Conversation Starters Section */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Conversation starters</h3>
            {starters.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">{starters.length}</Badge>
            )}
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Suggested prompts that help visitors begin a conversation.
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <Starters
            agId={agId}
            agentName={agentName}
            starters={starters}
            setStarters={setStarters}
            setNeedsUpdate={setNeedsUpdate}
            enableFollowUps={enableFollowUps}
            setEnableFollowUps={setEnableFollowUps}
            followUpInstructions={followUpInstructions}
            setFollowUpInstructions={setFollowUpInstructions}
            contextualStartersEnabled={contextualStartersEnabled}
            setContextualStartersEnabled={setContextualStartersEnabled}
          />
        </div>
      </div>

      {/* Escalation Buttons Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Escalation buttons</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Actions that redirect or escalate conversations when visitors need more help.
          </p>
        </div>
        <div className="overflow-hidden rounded-lg border bg-card">
          <EscalationButtons
            agId={agId}
            agentName={agentName}
            escalationButtons={escalationButtons}
            setEscalationButtons={setEscalationButtons}
            setNeedsUpdate={setNeedsUpdate}
            escalationEnabled={escalationEnabled}
            setEscalationEnabled={setEscalationEnabled}
          />
        </div>
      </div>

      {/* Security Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Rate limiting</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Limit message frequency to prevent automated abuse.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Limit to</span>
              <Input
                type="number"
                value={rateLimitMessages}
                onChange={(e) => setRateLimitMessages(Number(e.target.value))}
                className="w-20 text-center"
                min="1"
                max="100"
              />
              <span className="text-xs text-muted-foreground">messages every</span>
              <Input
                type="number"
                value={rateLimitSeconds}
                onChange={(e) => setRateLimitSeconds(Number(e.target.value))}
                className="w-20 text-center"
                min="1"
                max="3600"
              />
              <span className="text-xs text-muted-foreground">seconds</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Limit message</Label>
              <div className="relative">
                <Input
                  value={rateLimitMessage}
                  onChange={(e) => setRateLimitMessage(e.target.value)}
                  placeholder="Enter rate limit message..."
                  maxLength={200}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                  {rateLimitMessage.length}/200
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy & Legal Section */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Privacy &amp; legal</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Set the notice and policy link shown to visitors when they start a chat.
          </p>
        </div>

        <div className="overflow-hidden rounded-lg border bg-card divide-y">
          <div className="p-4">
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Disclaimer message</Label>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  Custom notice displayed when a visitor starts a chat.
                </p>
              </div>
              <div className="space-y-1">
                <Textarea
                  value={disclaimerMessage || ''}
                  onChange={(e) => setDisclaimerMessage?.(e.target.value)}
                  placeholder="e.g. By using this chat, you agree to our privacy policy..."
                  maxLength={500}
                  className="min-h-[80px] resize-none"
                />
                <span className="text-[10px] text-muted-foreground">
                  {disclaimerMessage?.length || 0}/500
                </span>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Privacy policy link</Label>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  URL linked from the disclaimer when one is provided.
                </p>
              </div>
              <Input
                value={privacyPolicyLink || ''}
                onChange={(e) => setPrivacyPolicyLink?.(e.target.value)}
                placeholder="https://yourwebsite.com/privacy-policy"
                type="url"
                className={cn(
                  privacyPolicyLink && !isValidUrl(privacyPolicyLink) && "border-red-500 focus:border-red-500"
                )}
              />
              {privacyPolicyLink && !isValidUrl(privacyPolicyLink) && (
                <span className="text-xs text-red-500">
                  Please enter a valid URL (e.g., https://example.com)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Visibility Section */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Visibility</h3>
            {visibilityRules.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">{visibilityRules.length} rule{visibilityRules.length !== 1 ? 's' : ''}</Badge>
            )}
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Control where the agent appears using page and country rules.
          </p>
        </div>

        <VisibilityRules
          visibilityRules={visibilityRules}
          setVisibilityRules={setVisibilityRules}
          showGloballyAfterEngagement={showGloballyAfterEngagement}
          setShowGloballyAfterEngagement={setShowGloballyAfterEngagement}
        />
      </div>
      </div>
    </div>
  );
}
